import type { Translate } from "../i18n/translate.ts";
import type { EvalHidProject } from "./types.ts";

/**
 * Name of the model assigned to a zone/sub-basin. A null `code` or `NO_MODEL`
 * (0, not a real APO_TIPO_MODELO code) shows as "unassigned" instead of a
 * cryptic "#0" — shared by the map (SubBasinMap.tsx) and the cards
 * (SubBasinCanvas.tsx) so both show the same info.
 *
 * `kind` (0 rainfall-runoff, 1 snow) is required, not optional: `modelTypeCode`
 * is only unique WITHIN a kind (code 1 is "Témez" for kind=0 but "Nieve1" for
 * kind=1, code 2 is "SAC"/"Nieve2" — see baseTemplate.ts's MODEL_TYPES), so a
 * kind-less lookup would silently return the wrong model's name for a snow
 * model type code that happens to collide with a rainfall-runoff one.
 */
export function modelTypeName(
  t: Translate,
  project: EvalHidProject,
  code: number | undefined | null,
  kind: number
): string {
  if (code == null || code === 0) return t("common.unassignedMissingZone");
  return (
    project.modelTypes.find((m) => m.modelTypeCode === code && m.kind === kind)?.modelTypeName ??
    `#${code}`
  );
}

export function zoneName(t: Translate, project: EvalHidProject, zoneCode: number | null): string {
  if (zoneCode == null) return t("common.unassigned");
  return project.zones.find((z) => z.zoneCode === zoneCode)?.name ?? `#${zoneCode}`;
}

export function outletPointName(
  t: Translate,
  project: EvalHidProject,
  outletCode: number | null
): string {
  if (outletCode == null) return t("common.unassigned");
  return project.outletPoints.find((p) => p.outletCode === outletCode)?.name ?? `#${outletCode}`;
}

export function aquiferName(project: EvalHidProject, aquiferCode: number): string {
  return project.aquifers.find((a) => a.aquiferCode === aquiferCode)?.name ?? `#${aquiferCode}`;
}
